import React, { useState, useEffect } from 'react';
import { exportFeatures, downloadFeaturesAsJSON } from '../services/exportService';
import './ExportFeatureDialog.css';

const ExportFeatureDialog = ({ isOpen, feature, onClose, onExport }) => {
  const [selectedChildren, setSelectedChildren] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && feature) {
      // Initialize all children as selected
      const children = feature.subFeatures || [];
      setSelectedChildren(children.map(() => true));
      setError(null);
    }
  }, [isOpen, feature]);

  if (!isOpen || !feature) return null;

  const hasChildren = feature.subFeatures && feature.subFeatures.length > 0;

  const handleToggleChild = (index) => {
    const newSelected = [...selectedChildren];
    newSelected[index] = !newSelected[index];
    setSelectedChildren(newSelected);
  };

  const handleSelectAll = () => {
    setSelectedChildren(feature.subFeatures.map(() => true));
  };

  const handleDeselectAll = () => {
    setSelectedChildren(feature.subFeatures.map(() => false));
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      setError(null);

      // Prepare feature for export
      const featuresToExport = [feature];
      const selectedChildrenMap = hasChildren 
        ? new Map([[feature.id, selectedChildren]])
        : new Map();
      
      const portableFeatures = exportFeatures(featuresToExport, selectedChildrenMap);
      downloadFeaturesAsJSON(portableFeatures);
      
      if (onExport) {
        onExport();
      }
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to export features');
      console.error('Error exporting features:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="export-feature-dialog-overlay" onClick={onClose}>
      <div className="export-feature-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="export-feature-dialog-header">
          <h3>Export Feature</h3>
          <button className="export-feature-dialog-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="export-feature-dialog-content">
          <div className="export-feature-info">
            <p><strong>Feature:</strong> {feature.name}</p>
            {feature.description && (
              <p><strong>Description:</strong> {feature.description}</p>
            )}
          </div>

          {hasChildren ? (
            <div className="export-feature-children">
              <div className="export-feature-children-header">
                <h4>Select Children to Export</h4>
                <div className="export-feature-children-actions">
                  <button
                    type="button"
                    className="select-all-button"
                    onClick={handleSelectAll}
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    className="deselect-all-button"
                    onClick={handleDeselectAll}
                  >
                    Deselect All
                  </button>
                </div>
              </div>
              <div className="export-feature-children-list">
                {feature.subFeatures.map((child, index) => (
                  <label key={child.id} className="export-feature-child-item">
                    <input
                      type="checkbox"
                      checked={selectedChildren[index] || false}
                      onChange={() => handleToggleChild(index)}
                    />
                    <span className="export-feature-child-name">{child.name}</span>
                    {child.description && (
                      <span className="export-feature-child-description">
                        {child.description.length > 100
                          ? child.description.substring(0, 100) + '...'
                          : child.description}
                      </span>
                    )}
                  </label>
                ))}
              </div>
            </div>
          ) : (
            <div className="export-feature-no-children">
              <p>This feature has no children. The feature itself will be exported.</p>
            </div>
          )}

          {error && (
            <div className="export-feature-error">
              <i className="fas fa-exclamation-circle"></i> {error}
            </div>
          )}
        </div>

        <div className="export-feature-dialog-footer">
          <button
            type="button"
            className="export-feature-cancel-button"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="export-feature-export-button"
            onClick={handleExport}
            disabled={loading}
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Exporting...
              </>
            ) : (
              <>
                <i className="fas fa-download"></i> Export
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportFeatureDialog;

